import Enumerable from "linq";
import {
  TimeMeasure,
  ICreatable,
  FactoryContainer,
  TypeBasedFactory,
} from "../../utilities";
import { ISize, PaintStyle, Color } from "../../domain/drawing";
import {
  IWizard,
  IWizardPage,
  IWizardContent,
  IWizardObjectIdentifiable,
  IWizardInputDecoratable,
  IWizardObjectOption,
  WizardContentTypes,
  IWizardAttributes,
  IWholeWizardAttributes,
} from "../../domain/format";
import {
  Wizard,
  WizardPage,
  WizardContentContainer,
  WizardContentHorizontalContainer,
  WizardContentStackContainer,
  WizardContentPatientIndicator,
  WizardContentFormatIndicator,
  WizardContentNotImplemented,
  WizardContentHeadingIndicator,
  WizardContentFixedTextIndicator,
  WizardContentMediaIndicator,
  WizardContentTextEditor,
  WizardContentTextInput,
  WizardContentNumberInput,
  WizardContentBarInput,
  WizardContentDateInput,
  WizardContentCheckSelector,
  WizardContentRadioSelector,
  WizardContentImageFileSelector,
  WizardContentImageCaptureInput,
  WizardContentClaioImageSelector,
  WizardContentClaioSchemaSelector,
  WizardContentMediaRemover,
  WizardContentMediaRotater,
  WizardObjectOption,
  IWizardContentOption,
  IWizardValidatable,
  WizardValidationAndOperator,
  WizardValidationNotOperator,
  WizardValidationOrOperator,
  WizardTextInputtedValidator,
  WizardDateInputtedValidator,
  WizardObjectSelectedValidator,
  WizardMediaSelectedValidator,
  WizardUserSelectedValidator,
  WizardOptionSelectedValidator,
  WizardObjectIdentifier,
  WizardInputDecoration,
  WizardContentTextIndicator,
  WizardContentUserSelector,
  WizardContentLoginUserSelector,
  WizardContentRadioSelectors,
  WizardContentImageSignInput,
} from "./Wizard";
import {
  IWizardBean,
  IWizardPageBean,
  IWizardContentBean,
  instanceOfIWizardContentContainerBean,
  instanceOfIWizardContentHorizontalContainerBean,
  instanceOfIWizardContentStackContainerBean,
  instanceOfIWizardContentPatientIndicatorBean,
  instanceOfIWizardContentFormatIndicatorBean,
  instanceOfIWizardContentNotImplementedBean,
  instanceOfIWizardContentHeadingIndicatorBean,
  instanceOfIWizardContentFixedTextIndicatorBean,
  instanceOfIWizardContentMediaIndicatorBean,
  instanceOfIWizardBeanTextSpecifiable,
  instanceOfIWizardContentTextEditorBean,
  instanceOfIWizardContentTextInputBean,
  instanceOfIWizardContentNumberInputBean,
  instanceOfIWizardContentBarInputBean,
  instanceOfIWizardContentDateInputBean,
  instanceOfIWizardContentRadioSelectorBean,
  instanceOfIWizardContentCheckSelectorBean,
  instanceOfIWizardContentImageFileSelectorBean,
  instanceOfIWizardContentImageCaptureInputBean,
  instanceOfIWizardContentClaioImageSelectorBean,
  instanceOfIWizardContentClaioSchemaSelectorBean,
  instanceOfIWizardContentMediaRemoverBean,
  instanceOfIWizardContentMediaRotaterBean,
  IWizardObjectOptionBean,
  IWizardValidatorBean,
  instanceOfIWizardAndOperatorBean,
  instanceOfIWizardOrOperatorBean,
  instanceOfIWizardNotOperatorBean,
  instanceOfIWizardIsTextInputtedValidatorBean,
  instanceOfIWizardIsDateInputtedValidatorBean,
  instanceOfIWizardIsSelectedValidatorBean,
  instanceOfIWizardIsMediaSelectedValidatorBean,
  instanceOfIWizardIsUserSelectedValidatorBean,
  IWizardBeanContentsContainable,
  IWizardBeanTextSpecifiable,
  IWizardBeanObjectIdSpecifiable,
  IWizardBeanSizeSpecifiable,
  IWizardBeanDecoratable,
  IWizardBeanObjectSelectable,
  instanceOfIWizardIsReferredFromFormatValidatorBean,
  instanceOfIWizardContentTextIndicatorBean,
  instanceOfIWizardContentUserSelectorBean,
  instanceOfIWizardContentLoginUserSelectorBean,
  instanceOfIWizardContentRadioSelectorsBean,
  instanceOfIWizardIsOptionSelectedValidatorBean,
  instanceOfIWizardBeanTypeSpecifiable,
  instanceOfIWizardContentImageSignInputBean,
  IWizardAttributesBean,
  IWholeWizardAttributesBean,
} from "./beans";
import { WizardFormatIdentifier, WizardReferredFromFormatValidator } from ".";

export class WizardTranslator {
  private _id: number = 0;

  private publishPrivateId(): string {
    return (++this._id).toString();
  }

  fromBean(source: IWizardBean): IWizard {
    return TimeMeasure.log(
      "wizardインスタンス生成",
      () => new Wizard(this.toWholeAttributes(source.attributes), this.toValidArray(source.pages, p => this.fromPageBean(p)), source.registration, this.toValidArray(source.registeredPages, p => this.fromPageBean(p)), source.scripts),
    );
  }

  private toAttributes(source?: IWizardAttributesBean): IWizardAttributes {
    return {
      fontSize: (source && source.fontSize) ? source.fontSize : undefined,
    };
  }

  private toWholeAttributes(source?: IWholeWizardAttributesBean): IWholeWizardAttributes {
    const attributes = this.toAttributes(source) as IWholeWizardAttributes;
    attributes.scales = source && source.scales ? source.scales : [];
    return attributes;
  }

  private toValidArray<TSource, TResult>(source: TSource[], to: (source: TSource) => TResult): TResult[] {
    return Enumerable.from(source)
      .select(s => to(s))
      .where(r => !!r)
      .toArray();
  }

  private fromPageBean(source: IWizardPageBean): IWizardPage {
    return new WizardPage(this.toAttributes(source.attributes), this.toValidArray(source.contents, c => this.fromContentBean(c)), {
      enableValidator: this.fromRootValidatorBean(source.enable),
      registerableValidator: this.fromRootValidatorBean(source.registerable),
    });
  }

  private fromRootValidatorBean(validators: IWizardValidatorBean[]): IWizardValidatable {
    return new WizardValidationAndOperator(this.toValidArray(validators, c => this.fromValidatorBean(c)));
  }

  private fromValidatorBean(source: IWizardValidatorBean): IWizardValidatable {
    return this._validatorFactory.create(source);
  }

  private fromContentBean(source: IWizardContentBean): IWizardContent {
    return this._contentFactory.create(source);
  }

  private _validatorFactory: ICreatable<IWizardValidatorBean, IWizardValidatable> = new FactoryContainer<IWizardValidatorBean, IWizardValidatable>(
    new TypeBasedFactory(instanceOfIWizardAndOperatorBean, s => new WizardValidationAndOperator(this.toValidArray(s.validators, v => this.fromValidatorBean(v)))),
    new TypeBasedFactory(instanceOfIWizardOrOperatorBean, s => new WizardValidationOrOperator(this.toValidArray(s.validators, v => this.fromValidatorBean(v)))),
    new TypeBasedFactory(instanceOfIWizardNotOperatorBean, s => new WizardValidationNotOperator(this.fromValidatorBean(s.validator))),
    new TypeBasedFactory(instanceOfIWizardIsReferredFromFormatValidatorBean, s => new WizardReferredFromFormatValidator(new WizardFormatIdentifier(s.formatId))),
    new TypeBasedFactory(instanceOfIWizardIsTextInputtedValidatorBean, s => new WizardTextInputtedValidator(new WizardObjectIdentifier(s.id))),
    new TypeBasedFactory(instanceOfIWizardIsDateInputtedValidatorBean, s => new WizardDateInputtedValidator(new WizardObjectIdentifier(s.id))),
    new TypeBasedFactory(instanceOfIWizardIsSelectedValidatorBean, s => new WizardObjectSelectedValidator(new WizardObjectIdentifier(s.id))),
    new TypeBasedFactory(instanceOfIWizardIsMediaSelectedValidatorBean, s => new WizardMediaSelectedValidator(new WizardObjectIdentifier(s.id))),
    new TypeBasedFactory(instanceOfIWizardIsUserSelectedValidatorBean, s => new WizardUserSelectedValidator(new WizardObjectIdentifier(s.id))),
    new TypeBasedFactory(instanceOfIWizardIsOptionSelectedValidatorBean, s => new WizardOptionSelectedValidator(new WizardObjectIdentifier(s.id), s.text)),
  );

  private _contentFactory: ICreatable<IWizardContentBean, IWizardContent> = new FactoryContainer<IWizardContentBean, IWizardContent>(
    this.createContainerContentFactory(instanceOfIWizardContentContainerBean, (i, a, o, c, s) => new WizardContentContainer(i, a, c, o, s.gap, s.contentMargin)),
    this.createContainerContentFactory(instanceOfIWizardContentHorizontalContainerBean, (i, a, o, c, s) => new WizardContentHorizontalContainer(i, a, c, o, s.gap, s.contentMargin)),
    this.createContainerContentFactory(instanceOfIWizardContentStackContainerBean, (i, a, o, c, s) => new WizardContentStackContainer(i, a, c, this.fromSizeBean(s), o)),
    this.createContentFactory(instanceOfIWizardContentPatientIndicatorBean, (i, a, o) => new WizardContentPatientIndicator(i, a, o)),
    this.createContentFactory(instanceOfIWizardContentFormatIndicatorBean, (i, a, o) => new WizardContentFormatIndicator(i, a, o)),
    this.createLabelledContentFactory(instanceOfIWizardContentHeadingIndicatorBean, (i, a, o, l) => new WizardContentHeadingIndicator(i, a, l, o)),
    this.createLabelledContentFactory(instanceOfIWizardContentFixedTextIndicatorBean, (i, a, o, l) => new WizardContentFixedTextIndicator(i, a, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentMediaIndicatorBean, (i, a, o, r, l, s) => new WizardContentMediaIndicator(i, a, r, this.fromSizeBean(s), l, o, this.fromContentBean(s.processor))),
    this.createObjectAccessibleContentFactory(instanceOfIWizardContentTextEditorBean, (i, a, o, r) => new WizardContentTextEditor(i, a, r, o)),
    this.createObjectDecoratableContentFactory(instanceOfIWizardContentTextInputBean, (i, a, o, r, d) => new WizardContentTextInput(i, a, r, d, o)),
    this.createObjectDecoratableContentFactory(instanceOfIWizardContentNumberInputBean, (i, a, o, r, d) => new WizardContentNumberInput(i, a, r, d, o)),
    this.createObjectAccessibleContentFactory(instanceOfIWizardContentBarInputBean, (i, a, o, r, s) => new WizardContentBarInput(i, a, r, this.fromSizeBean(s), s.range, s.stepCount, s.direction, new PaintStyle(new Color(s.color)), o)),
    this.createObjectDecoratableContentFactory(instanceOfIWizardContentDateInputBean, (i, a, o, r, d) => new WizardContentDateInput(i, a, r, d, o)),
    this.createObjectSelectableContentFactory(instanceOfIWizardContentRadioSelectorBean, (i, a, o, os, s) => new WizardContentRadioSelector(i, a, os, o, !!s.isHorizontal)),
    this.createObjectSelectableContentFactory(instanceOfIWizardContentCheckSelectorBean, (i, a, o, os, s) => new WizardContentCheckSelector(i, a, os, o, !!s.isHorizontal)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentImageFileSelectorBean, (i, a, o, r, l) => new WizardContentImageFileSelector(i, a, r, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentImageCaptureInputBean, (i, a, o, r, l) => new WizardContentImageCaptureInput(i, a, r, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentImageSignInputBean, (i, a, o, r, l, s) => new WizardContentImageSignInput(i, a, r, this.fromSizeBean(s), l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentClaioImageSelectorBean, (i, a, o, r, l) => new WizardContentClaioImageSelector(i, a, r, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentClaioSchemaSelectorBean, (i, a, o, r, l) => new WizardContentClaioSchemaSelector(i, a, r, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentMediaRemoverBean, (i, a, o, r, l) => new WizardContentMediaRemover(i, a, r, l, o)),
    this.createObjectAccessibleLabelledContentFactory(instanceOfIWizardContentMediaRotaterBean, (i, a, o, r, l, s) => new WizardContentMediaRotater(i, a, r, l, s.angle, o)),
    this.createObjectDecoratableContentFactory(instanceOfIWizardContentTextIndicatorBean, (i, a, o, r, d) => new WizardContentTextIndicator(i, a, r, d, o)),
    this.createObjectAccessibleContentFactory(instanceOfIWizardContentUserSelectorBean, (i, a, o, r, s) => new WizardContentUserSelector(i, a, r, o, s.isMultiSelectable)),
    this.createObjectAccessibleContentFactory(instanceOfIWizardContentLoginUserSelectorBean, (i, a, o, r, s) => new WizardContentLoginUserSelector(i, a, r, o)),
    this.createObjectAccessibleContentFactory(instanceOfIWizardContentRadioSelectorsBean, (i, a, o, r, s) => new WizardContentRadioSelectors(i, a, r, o, s.isHorizontal, s.visibleOptionIndexes, s.alternativeOptions)),
    this.createNotImplementedContentFactory(instanceOfIWizardContentNotImplementedBean, (i, a, t, o) => new WizardContentNotImplemented(i, a, t, o)),
  );

  private createContentFactory<T extends IWizardContentBean>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return new TypeBasedFactory(is, s => to(this.publishPrivateId(), this.toAttributes(s.attributes), this.fromContentOptionBean(s), s));
  }

  private createContainerContentFactory<T extends IWizardContentBean & IWizardBeanContentsContainable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, c: IWizardContent[], s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createContentFactory(is, (i, a, o, s) => to(i, a, o, this.toValidArray(s.contents, c => this.fromContentBean(c)), s));
  }

  private createLabelledContentFactory<T extends IWizardContentBean & IWizardBeanTextSpecifiable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, l: string, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createContentFactory(is, (i, a, o, s) => to(i, a, o, s.text, s));
  }

  private createObjectAccessibleContentFactory<T extends IWizardContentBean & IWizardBeanObjectIdSpecifiable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, r: IWizardObjectIdentifiable, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createContentFactory(is, (i, a, o, s) => to(i, a, o, this.fromObjectIdentifierBean(s), s));
  }

  private createObjectDecoratableContentFactory<T extends IWizardContentBean & IWizardBeanObjectIdSpecifiable & IWizardBeanDecoratable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, r: IWizardObjectIdentifiable, d: IWizardInputDecoratable, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createObjectAccessibleContentFactory(is, (i, a, o, r, s) => to(i, a, o, r, this.fromObjectDecorationBean(s), s));
  }

  private createObjectAccessibleLabelledContentFactory<T extends IWizardContentBean & IWizardBeanObjectIdSpecifiable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, r: IWizardObjectIdentifiable, l: string, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createObjectAccessibleContentFactory(is, (i, a, o, r, s) => to(i, a, o, r, instanceOfIWizardBeanTextSpecifiable(s) ? s.text : null, s));
  }

  private createObjectSelectableContentFactory<T extends IWizardContentBean & IWizardBeanObjectSelectable>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, o: IWizardContentOption, os: IWizardObjectOption[], s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return this.createContentFactory(is, (i, a, o, s) => to(i, a, o, this.toValidArray(s.options, op => this.fromObjectOptionBean(op)), s));
  }

  private createNotImplementedContentFactory<T extends IWizardContentBean>(is: (s: IWizardContentBean) => s is T, to: (i: string, a: IWizardAttributes, t: WizardContentTypes, o: IWizardContentOption, s: T) => IWizardContent): ICreatable<IWizardContentBean, IWizardContent> {
    return new TypeBasedFactory(is, s => to(this.publishPrivateId(), this.toAttributes(s.attributes), this.toWizardContentTypes(s), this.fromContentOptionBean(s), s));
  }

  private toWizardContentTypes(source: IWizardContentBean): WizardContentTypes {
    return instanceOfIWizardBeanTypeSpecifiable(source)
      ? source.type as WizardContentTypes
      : "not-implemented";
  }

  private fromContentOptionBean(source: IWizardContentBean): IWizardContentOption {
    return {
      margin: source.margin,
      isMandatory: source.isMandatory,
      visibleValidator: this.fromRootValidatorBean(source.visible),
      enableValidator: this.fromRootValidatorBean(source.enable),
      registerableValidator: this.fromRootValidatorBean(source.registerable),
    };
  }

  private fromObjectIdentifierBean(source: IWizardBeanObjectIdSpecifiable): IWizardObjectIdentifiable {
    return new WizardObjectIdentifier(source.id);
  }

  private fromSizeBean(source: IWizardBeanSizeSpecifiable): ISize<string> {
    return source.size
      ? {
        width: source.size.width,
        height: source.size.height,
      }
      : null;
  }

  private fromObjectDecorationBean(source: IWizardBeanDecoratable): IWizardInputDecoratable {
    return new WizardInputDecoration(source.prefix, source.postfix);
  }

  private fromObjectOptionBean(source: IWizardObjectOptionBean): IWizardObjectOption {
    return new WizardObjectOption(this.fromObjectIdentifierBean(source), {
      content: this.fromContentBean(source.content),
      text: source.text,
    });
  }
}
